import isUndefined from 'lodash-es/isUndefined';
import { identity } from 'rxjs';
import { cleanListItemElements } from '../../helpers/cleanListItemElements';
import { isFileDataTransfer } from '../../helpers/dataTransfer';
import { htmlFromDom } from '../../helpers/dom/htmlFromDom';
import { dirtyHtmlToDom } from '../../helpers/dom/htmlToDom';
import { clipSelection } from '../../helpers/elementHtmlEditor/clipboard';
import { flattenParagraphs } from '../../helpers/flattenParagraphs';
import { markdownAstFromDom } from '../../helpers/markdown/markdownAstFromDom';
import { markdownAstToDom } from '../../helpers/markdown/markdownAstToDom';
import { markdownToDom } from '../../helpers/markdown/markdownToDom';
import { InteractionHandler } from '../../interactionEvent';
import { createElementBlocksAsYouTypeService } from '../../services/dom/elementBlocksAsYouTypeService';
import { createElementCodeBlockService } from '../../services/dom/elementCodeBlockService';
import { createElementInlineCodeService } from '../../services/dom/elementInlineCodeService';
import { createElementMarkdownHtmlService } from '../../services/dom/elementMarkdownHtmlService';
import { createElementMarkdownService } from '../../services/dom/elementMarkdownService';
import { createElementParagraphTextUnderCursorService } from '../../services/dom/elementParagraphTextUnderCursorService';
import { createHtmlAutolinkService } from '../../services/html/htmlAutolinkService';
import { createHtmlBlockquoteService } from '../../services/html/htmlBlockquoteService';
import { createHtmlImageService } from '../../services/html/htmlImageService';
import { createHtmlLinkService } from '../../services/html/htmlLinkService';
import { createHtmlListService } from '../../services/html/htmlListService';
import { createHtmlPasteLinkService } from '../../services/html/htmlPasteLinkService';
import { createHtmlSpaceIndentService } from '../../services/html/htmlSpaceIndentService';
import { createElementStyleAsYouTypeService } from '../../services/rich/elementStyleAsYouTypeService';
import { createElementStyleService } from '../../services/rich/elementStyleService';
import { createAutocompleteService } from '../../services/text/autocompleteService';
import { createCombinationIndentService } from '../../services/text/indentService';
import { ContentEditableEditor, createContentEditableEditor } from '../base/contentEditableEditor';
import { EditorSpec } from '../base/editor';
import { ContentEditableEditorPlugin } from '../base/plugins/contentEditableEditorPlugin';
import { MarkdownEditor, MarkdownEditorServices } from './markdownEditor';
import { createContentEditableMarkdownBeforePlugin } from './plugins/contentEditableMarkdownBeforePlugin';

const FLATTEN_MAX_PARAGRAPH_COUNT = 100;

export interface ContentEditableMarkdownEditor<S extends MarkdownEditorServices = MarkdownEditorServices>
  extends ContentEditableEditor<S>,
    MarkdownEditor<S> {}

export type ContentEditableMarkdownEditorOptions = {
  shouldLineBreakOnShiftEnter?: () => boolean;
  shouldPasteAsText?: boolean;
  areStyleKeyboardShortcutsDisabled?: boolean;
  plugins?: ReadonlyArray<ContentEditableMarkdownEditorPlugin>;
  shouldNativeDelete?: boolean;
  hasNewDeleteSelection?: boolean;
  onInteraction?: InteractionHandler;
  hasListServiceV2?: boolean;
};

export interface ContentEditableMarkdownEditorPlugin extends ContentEditableEditorPlugin {}

type ContentEditableMarkdownEditorSpecCreator<S extends MarkdownEditorServices> = (
  spec: EditorSpec<MarkdownEditorServices>,
) => EditorSpec<S>;

export function createContentEditableMarkdownEditor<S extends MarkdownEditorServices>(
  options: ContentEditableMarkdownEditorOptions,
  createSpec: ContentEditableMarkdownEditorSpecCreator<S>,
): ContentEditableMarkdownEditor<S>;
export function createContentEditableMarkdownEditor(
  options?: ContentEditableMarkdownEditorOptions,
): ContentEditableMarkdownEditor;
export function createContentEditableMarkdownEditor<
  S extends MarkdownEditorServices = MarkdownEditorServices
>(
  options: ContentEditableMarkdownEditorOptions = {},
  createSpec: ContentEditableMarkdownEditorSpecCreator<
    S
  > = identity as ContentEditableMarkdownEditorSpecCreator<S>,
): ContentEditableMarkdownEditor<S> {
  const customPlugins = options.plugins || [];
  const plugins = [
    createContentEditableMarkdownBeforePlugin(),
    ...customPlugins,
    createCoreAfterPlugin(options),
  ];

  return createContentEditableEditor(
    {
      plugins,
      shouldNativeDelete: options.shouldNativeDelete,
      hasNewDeleteSelection: options.hasNewDeleteSelection,
      shouldKeepPreformattedText: true,
      onInteraction: options.onInteraction,
      shouldPasteAsTextInCodeBlocks: true,
    },
    (baseSpec, element$) => {
      const { services: baseServices, privateServices: basePrivateServices = [] } = baseSpec;
      const {
        html: htmlService,
        commit: commitService,
        selection: selectionService,
        undo: undoService,
      } = baseServices;

      const markdownService = createElementMarkdownService(element$, htmlService);

      const textUnderCursorService = createElementParagraphTextUnderCursorService(selectionService);

      const styleService = createElementStyleService(element$, selectionService, {
        isUnderlineDisabled: true,
        areKeyboardShortcutsDisabled: options.areStyleKeyboardShortcutsDisabled,
        onInteraction: options.onInteraction,
      });
      const styleAsYouTypeService = createElementStyleAsYouTypeService(
        commitService,
        selectionService,
        undoService,
        { onInteraction: options.onInteraction },
      );
      const inlineCodeService = createElementInlineCodeService(element$, selectionService, undoService, {
        onInteraction: options.onInteraction,
      });

      const linkService = createHtmlLinkService(element$, selectionService);
      const autoLinkService = createHtmlAutolinkService(element$, selectionService, {
        onInteraction: options.onInteraction,
      });
      const pasteLinkService = createHtmlPasteLinkService(element$, selectionService, linkService);

      const listService = createHtmlListService(element$, selectionService, {
        shouldLineBreakOnShiftEnter: options.shouldLineBreakOnShiftEnter,
        hasListServiceV2: options.hasListServiceV2,
      });
      const blockquoteService = createHtmlBlockquoteService(element$, selectionService);
      const spaceIndentService = createHtmlSpaceIndentService(
        element$,
        selectionService,
        textUnderCursorService,
        textUnderCursorService,
        {},
        autoLinkService,
      );
      const indentService = createCombinationIndentService(
        listService,
        blockquoteService,
        spaceIndentService,
      );
      const blocksAsYouTypeService = createElementBlocksAsYouTypeService(
        element$,
        selectionService,
        textUnderCursorService,
        blockquoteService,
        listService,
        undoService,
        { onInteraction: options.onInteraction },
      );
      const codeBlockService = createElementCodeBlockService(element$, selectionService, undoService, {
        onInteraction: options.onInteraction,
      });

      const autocompleteService = createAutocompleteService(textUnderCursorService);

      const imageService = createHtmlImageService(element$, selectionService, undoService);

      return createSpec({
        ...baseSpec,
        services: {
          ...baseServices,
          markdown: markdownService,
          html: createElementMarkdownHtmlService(
            element$,
            onDone => htmlService.waitForChanges(onDone),
            markdownService,
          ),
          textUnderCursor: textUnderCursorService,
          autocomplete: autocompleteService,
          style: styleService,
          link: linkService,
          indent: indentService,
          quote: blockquoteService,
          list: listService,
          image: imageService,
        },
        privateServices: [
          styleAsYouTypeService,
          inlineCodeService,
          autoLinkService,
          pasteLinkService,
          blocksAsYouTypeService,
          codeBlockService,
          ...basePrivateServices,
        ],
      });
    },
  );
}

const createCoreAfterPlugin = (
  options: ContentEditableMarkdownEditorOptions,
): ContentEditableMarkdownEditorPlugin => ({
  processTree: element => {
    flattenParagraphs(element, {
      maxParagraphCount: FLATTEN_MAX_PARAGRAPH_COUNT,
    });
    cleanListItemElements(element);

    // Conservatively remove styles by removing style attributes from spans where it's the only attribute.
    for (const span of element.querySelectorAll('span[style]')) {
      if (span.attributes.length > 1) {
        continue;
      }

      span.removeAttribute('style');
    }
  },
  handleDataTransfer: (dataTransfer, editService) => {
    // If this appears to be a file upload/drop, then ignore any HTML versions and let the event bubble.
    if (isFileDataTransfer(dataTransfer)) {
      return false;
    }

    // Prefer Markdown, if present.
    const markdown = dataTransfer.getData('text/markdown');
    if (markdown) {
      const cleanHtml = htmlFromDom(document => markdownToDom(document, markdown));
      // TODO: Can we render the Markdown straight into the DOM?
      editService.insertCleanHtml(cleanHtml);
      return true;
    }

    // Convert HTML to Markdown, if present.
    const dirtyHtml = dataTransfer.getData('text/html');
    if (dirtyHtml && !options.shouldPasteAsText) {
      // Sanitize and serialize the HTML, then convert it to a Markdown AST.
      const markdownAst = dirtyHtmlToDom(dirtyHtml, container => markdownAstFromDom(container));
      // Render the Markdown AST to back to HTML, which is now sanitized and Markdown-compatible.
      const cleanHtml = htmlFromDom(document => markdownAstToDom(document, markdownAst));
      // TODO: Can we render the Markdown AST straight into the DOM?
      editService.insertCleanHtml(cleanHtml);
      return true;
    }

    // Fall back to plain text, if present.
    const text = dataTransfer.getData('text/plain');
    if (text) {
      editService.insertText(text);
      return true;
    }

    return false;
  },
  populateDataTransfer: (dataTransfer, element) => {
    const selection = element.ownerDocument && element.ownerDocument.getSelection();
    const clipping = clipSelection(element, selection, { shouldReturnMarkdown: true });
    if (!clipping) {
      return false;
    }

    dataTransfer.setData('text/html', clipping.html);
    dataTransfer.setData('text/plain', clipping.text);

    if (!isUndefined(clipping.markdown)) {
      dataTransfer.setData('text/markdown', clipping.markdown);
    }

    return true;
  },
});
